import {GDPR} from './GDPR';
import {URLManager} from './URLManager';

declare var window: any;

interface ISessionCookie {
    name: string;
    value: string;
}

interface ILocalCookie {
    name: string;
    value: string;
}

interface IScript {
    src: string;
    id: string;
    callback: any;
    async: boolean;
}

export class StorageUtility {
    private static instance: StorageUtility;

    static getInstance() {
        if (!StorageUtility.instance) {
            StorageUtility.instance = new StorageUtility();
        }
        return StorageUtility.instance;
    }

    cookieQueue: any;
    scriptQueue: any;
    sessionQueue: any;
    localQueue: any;

    private constructor() {
        this.cookieQueue = [];
        this.scriptQueue = [];
        this.sessionQueue = [];
        this.localQueue = [];
        this.saveLeads();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Checks for browser storage accessability, mainly used for IE.
     * @since 14.8.0
     */
    public isAccessable() {
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem('keeper_test', 'yes');
                if (localStorage.getItem('keeper_test') === 'yes') {
                    localStorage.removeItem('keeper_test');
                    return true;
                } else return false;
            } catch (e) {
                return false;
            }
        } else {
            return false;
        }
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Checks for storage availability against GDPR regulations.
     * @since 14.8.0
     */
    public isAvailable() {
        return GDPR.getInstance().hasConsent();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Clears local, session, and cookie storage for the website.
     * @since 12.0ß.0
     */
    public clear() {
        localStorage.clear();
        sessionStorage.clear();
        this.clearCookies();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Creates a new cookie.
     * @param {string} name - The cookie name.
     * @param {string} value - The cookie value.
     * @param {string} days - The amount of days until cookie expiration
     * @since 12.0.0
     */
    public setCookie(name: string, value: string, days: number = null) {
        if (!name || !value) {
            return;
        }

        let expires = '';

        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
        }

        this.queueCookie(name + '=' + value + expires + '; path=/');
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Returns a cookie matching the given name.
     * @param {string} name - The cookie name.
     * @since 12.0.0
     */
    public getCookie(name: string) {
        const stagedName = name + '=';
        const cookies = document.cookie.split(';');
        let itemToReturn = null;
        cookies.forEach(cookie => {
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1, cookie.length);
            }
            if (cookie.indexOf(stagedName) === 0 && itemToReturn == null) {
                itemToReturn = cookie.substring(stagedName.length, cookie.length);
            }
        });
        return itemToReturn;
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Adds the given cookie to the cookie queue.
     * @param {string} cookie - The cookie string containing the name, value, and expiration.
     * @since 12.0.0
     */
    private queueCookie(cookie: string) {
        this.cookieQueue.push(cookie);
        this.refresh();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Clears all cookies.
     * @since 12.0.0
     */
    public clearCookies() {
        if (document.cookie) {
            // remove all cookies on current domain
            document.cookie.split(';').forEach(cookie => {
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
            });

            // remove all cookies on keepersecurity domain
            document.cookie.split(';').forEach(cookie => {
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                document.cookie =
                    name +
                    '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;domain=.keepersecurity.com;path=/';
            });
        }
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Creates a new session cookie.
     * @param {string} name - The session cookie name.
     * @param {string} value - The session cookie value.
     * @since 12.0.0
     */
    public setSession(name: string, value: string) {
        if (!name || !value) {
            return;
        }

        this.queueSession(name, value);
    }

    /**
     * @summary Creates a new session cookie.
     * @param {object} sessionObj - An object that holds the names/values for the session cookie.
     * @since 16.0.0
     */
    public setSessionMultiple(sessionObj: Object) {
        let pairsAdded = 0;
        for (let name in sessionObj) {
            const value = sessionObj[name];
            if (name && value) {
                pairsAdded++;
                this.sessionQueue.push({
                    name,
                    value,
                });
            }
        }

        if (pairsAdded) this.refresh();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Returns a session cookie matching the given name.
     * @param {string} name - The session cookie name.
     * @since 12.0.0
     */
    public getSession(name: string) {
        let itemtoReturn = null;
        if (!sessionStorage[name]) {
            this.sessionQueue.forEach((session, i) => {
                if (session.name === name && itemtoReturn == null) {
                    itemtoReturn = session.value;
                }
            });
        }

        return itemtoReturn != null ? itemtoReturn : sessionStorage[name];
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Adds the given session cookie to the session cookie queue.
     * @param {string} name - The session cookie name.
     * @param {string} value - The session cookie value.
     * @since 12.0.0
     */
    queueSession(name: string, value: string) {
        this.sessionQueue.push({
            name,
            value,
        });

        this.refresh();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Removes the given session cookie.
     * @param {string} name - The session cookie name.
     * @since 12.0.0
     */
    removeSession(name: string) {
        this.sessionQueue.forEach((session, i) => {
            if (name === session.name) {
                this.sessionQueue.splice(i, 1);
            }
        });
        sessionStorage.removeItem(name);
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Creates a new session cookie.
     * @param {string} name - The session cookie name.
     * @param {string} value - The session cookie value.
     * @since 12.0.0
     */
    public setLocal(name: string, value: string) {
        if (!name || !value) {
            return;
        }

        this.queueLocal(name, value);
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Returns a local cookie matching the given name.
     * @param {string} name - The local cookie name.
     * @since 12.0.0
     */
    public getLocal(name: string) {
        let itemtoReturn = null;
        if (!localStorage[name]) {
            this.localQueue.forEach((local, i) => {
                if (local.name === name) {
                    itemtoReturn = local.value;
                }
            });
        }
        return itemtoReturn != null ? itemtoReturn : localStorage[name];
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Adds the given local cookie to the local cookie queue.
     * @param {string} name - The local cookie name.
     * @param {string} value - The local cookie value.
     * @since 12.0.0
     */
    queueLocal(name: string, value: string) {
        this.localQueue.push({
            name,
            value,
        });

        this.refresh();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Removes the given local cookie.
     * @param {string} name - The local cookie name.
     * @since 12.0.0
     */
    public removeLocal(name: string) {
        this.localQueue.forEach((local, i) => {
            if (name === local.name) {
                this.localQueue.splice(i, 1);
            }
        });
        localStorage.removeItem(name);
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Checks to see if storage is available. If so, it will load everthing in queue.
     * @since 14.8.0
     */
    public refresh() {
        if (this.isAvailable()) {
            this.loadFromQueues();
        }
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Loads all items within queues.
     * @since 14.8.0
     */
    public loadFromQueues() {
        this.localQueue.forEach((local, i) => {
            localStorage.setItem(local.name, local.value);
        });

        this.sessionQueue.forEach((session, i) => {
            sessionStorage.setItem(session.name, session.value);
        });

        this.cookieQueue.forEach((cookie, i) => {
            document.cookie = cookie;
        });

        this.localQueue.splice(0, this.localQueue.length);
        this.sessionQueue.splice(0, this.sessionQueue.length);
        this.cookieQueue.splice(0, this.cookieQueue.length);

        this.loadScripts();
    }

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Loads all scripts within queue.
     * @since 14.8.0
     */
    public loadScripts = () => {
        if (URLManager.getInstance().getParams()['email']) {
            return;
        }

        this.scriptQueue.forEach((item: any) => {
            if (!document.getElementById(item.id)) {
                const head = document.getElementsByTagName('head')[0];
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = item.src;
                script.id = item.id;
                if (item.async === true) script.async = true;
                head.appendChild(script);
                if (item.callback && item.callback !== null) {
                    script.addEventListener('load', item.callback);
                }
            }
        });

        this.scriptQueue.splice(0, this.scriptQueue.length);
    };

    /////////////////////////////////////////////////////////////////////

    /**
     * @summary Adds script to the script queue.
     * @param {string} src - The path for the source of the script.
     * @param {string} id - The html attribute id of the script.
     * @param {Function} callback - Callback to execute once the page has loaded.
     * @param {boolean} async - Async attribute on script.
     * @since 14.8.0
     */
    public setScript = (
        src: string,
        id: string,
        callback: Function = null,
        async: boolean = false
    ) => {
        const script: IScript = {
            src,
            id,
            callback,
            async,
        };

        if (!this.scriptQueue) {
            this.scriptQueue = [script];
        } else {
            this.scriptQueue.push(script);
        }
    };

    /////////////////////////////////////////////////////////////////////

    public saveLeads() {
        if (URLManager.getInstance().getParams()['lead_source']) {
            this.setLocal('lead_source', URLManager.getInstance().getParams()['lead_source']);

            this.setLocal('lead_source_params', location.search);
        }
    }
}
